import { useEffect, useMemo, useRef, useState } from 'react';
import type { Searchable } from '@versesverse/core';
import { searchNodes } from './searchNodes';
import { typeToSearch } from './typeToSearch';
import { passageFor, type HoldsCitation } from './passageTerm';
import { useLang } from '../i18n/lang';

/*
 * THE KEYBOARD DOOR INTO A CANVAS MAP.
 *
 * The map cannot be tabbed to or read aloud, so this is how a keyboard or screen-reader user finds
 * a figure: a combobox that lists matches and, on Enter or click, selects one. It follows the ARIA
 * combobox pattern — aria-expanded, aria-controls, aria-activedescendant — so the active option is
 * announced as the user arrows through it, rather than the list being a visual-only affordance.
 */
export interface SearchBoxProps {
  nodes: Searchable[];
  onSelect: (id: string) => void;
  /**
   * WHAT EACH RESULT IS, in the same sentence the map says under the pointer.
   *
   * A row reading only "Abraham" makes a reader choose between things they cannot tell apart —
   * Joshua and Jesus are the same name in the King James twice over, and there are 21 such
   * collisions. Theirs answers it in the row: "Person · 4 corpora", with the scripture's colour
   * beside it (universe-v8.dc.html, the search list).
   *
   * The sentence is `map/v8/hover.ts`'s, deliberately: the search row and the card under the pointer
   * answer the same question about the same figure, and two spellings of that answer would be two
   * things to keep in step. Absent means the plain list, which is what the old renderer wants.
   */
  /* `also` is the figure's other name, when the row's own name does not already contain it. */
  describe?: (node: Searchable) => { sub: string; colour: string; also?: string } | null;
  /**
   * A CITATION IS NOT A NAME, and it was the one thing a reader could type that this box answered
   * with silence — while the library publishes 1,556 pages addressed by one and the app has a view
   * built for it. Optional together: without them the box is exactly what it was.
   */
  holdsCitation?: HoldsCitation;
  onOpenPassage?: (ref: string) => void;
}

const LISTBOX_ID = 'map-search-results';

export function SearchBox({ nodes, onSelect, describe, holdsCitation, onOpenPassage }: SearchBoxProps): React.JSX.Element {
  const { t } = useLang();
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const results = useMemo(() => searchNodes(nodes, term), [nodes, term]);
  const open = results.length > 0;
  /*
   * TYPING SOMETHING WITH NO MATCH USED TO DO NOTHING AT ALL, and nothing is what a broken control
   * looks like.
   *
   * The list opens only when there are results, so a reader who mistypes a name, or reaches for a
   * figure this library does not carry, or types a word expecting a full-text search, watched the
   * box sit there and concluded the search does not work. On the one control the map's own note
   * calls "the one control a reader uses on every visit".
   *
   * It is this repository's oldest lesson turned around to face the reader: an empty result and a
   * search that never ran print identically. The fix there is a gate that says how much it looked
   * at; the fix here is a line that says it looked.
   */
  const nothing = term.trim() !== '' && results.length === 0;
  /* Only consulted where the names found nothing: a term that is both a name and a citation is a
     name, because that is what this box is for. */
  const citation = useMemo(
    () => (nothing && holdsCitation ? passageFor(term, holdsCitation) : undefined),
    [nothing, holdsCitation, term],
  );

  const choose = (index: number): void => {
    const chosen = results[index];
    if (!chosen) return;
    onSelect(chosen.id);
    setTerm('');
    setActive(-1);
    inputRef.current?.focus();
  };

  /*
   * START TYPING ANYWHERE, AND YOU ARE SEARCHING — theirs, engine.js:682.
   *
   * The map is a canvas with one text field over it, so a printable key pressed on the page can only
   * ever have meant one thing. Ours made a reader aim at the box and click it first.
   *
   * Listened for on the window rather than on the map, because the search must answer whether or not
   * the map has focus — it is the keyboard's door into a canvas, and a door that only opens when you
   * are already inside is not one. `typeToSearch` holds every reason not to fire.
   */
  useEffect(() => {
    function onType(event: KeyboardEvent): void {
      const target = event.target;
      const el = target instanceof HTMLElement ? target : null;
      if (!typeToSearch({
        key: event.key,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        tag: el?.tagName ?? '',
        editable: el?.isContentEditable ?? false,
      })) return;
      const box = inputRef.current;
      if (box === null || document.activeElement === box) return;
      event.preventDefault();
      box.focus();
      setTerm(event.key);
      setActive(-1);
    }
    window.addEventListener('keydown', onType);
    return () => { window.removeEventListener('keydown', onType); };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(a => Math.min(a + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open) choose(active >= 0 ? active : 0);
    } else if (event.key === 'Escape') {
      setTerm('');
      setActive(-1);
    }
  };

  return (
    <div className="search">
      <input
        ref={inputRef}
        type="text"
        className="search-input"
        role="combobox"
        aria-label={t('search.label')}
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 && results[active] ? `opt-${results[active].id}` : undefined}
        placeholder={t('search.placeholder')}
        value={term}
        onChange={e => { setTerm(e.target.value); setActive(-1); }}
        onKeyDown={onKeyDown}
      />
      {/*
        * A STATUS, NOT AN EMPTY LISTBOX. A combobox popup with no options is a thing a screen reader
        * announces as a list of nothing; `role="status"` announces the sentence itself, which is
        * what a reader who typed and got nowhere actually needs. `aria-expanded` above stays keyed
        * to the options, because that is what it describes.
        */}
      {nothing && (
        <p className="search-nothing" role="status">{t('search.nothing', { term: term.trim() })}</p>
      )}
      {/* OUTSIDE the live region, as `CenteredStatus` puts its actions: the sentence is announced,
          and the control is then found by a reader moving through it rather than read out as part
          of the message. The sentence above stays true — a citation is not a name. */}
      {citation !== undefined && onOpenPassage && (
        <button
          type="button"
          className="search-passage"
          /* onMouseDown for the reason the options use it: click would blur the input first. */
          onMouseDown={e => { e.preventDefault(); onOpenPassage(citation); setTerm(''); setActive(-1); }}
        >
          {t('search.openPassage', { ref: citation })}
        </button>
      )}
      {open && (
        <ul className="search-results" id={LISTBOX_ID} role="listbox">
          {results.map((node, i) => (
            <li
              key={node.id}
              id={`opt-${node.id}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'search-option active' : 'search-option'}
              /* onMouseDown, not onClick — click would first blur the input and close the list. */
              onMouseDown={e => { e.preventDefault(); choose(i); }}
            >
              {(() => {
                const said = describe?.(node) ?? null;
                if (said === null) return node.name;
                return (
                  <>
                    <span className="search-dot" style={{ backgroundColor: said.colour }} aria-hidden="true" />
                    <span className="search-option-name">{node.name}</span>
                    {/*
                      * THE OTHER NAME THIS FIGURE IS KNOWN BY, when there is one.
                      *
                      * Theirs writes both in the row — "Joseph / Yusuf", "Jonah / Yunus", "Job /
                      * Ayyub" — and a reader searching in one tradition's spelling meets the other
                      * one here, which is most of what this library is for. Ours wrote the English
                      * alone.
                      *
                      * The caller decides what the second name IS: this only agrees to show one.
                      *
                      * IT CARRIES ITS OWN SPACE, for the reason the one below it does. The slash a
                      * reader sees comes from `::before` in the stylesheet, and generated content is
                      * not in `textContent` — so without this the row is announced as "AdamMusa",
                      * which is the same fault the space below was added to fix, one span earlier.
                      */}
                    {said.also !== undefined && said.also !== '' && (
                      <>
                        {' '}
                        <span className="search-option-also">{said.also}</span>
                      </>
                    )}
                    {/*
                      * A SPACE, FOR THE REASON `MapHoverCard` CARRIES ONE. The two spans abut in
                      * `textContent`, so this option read back as "AdamPerson · 5 corpora" — the
                      * name and what it is running into one word, which is what a screen reader is
                      * handed for the row it announces. It changes nothing on screen; the sub
                      * already has its own inline margin.
                      *
                      * The card was fixed and this was not, because `describe` reached only the
                      * ported renderer until now — so the fault existed on the search no reader
                      * could get to, and wiring it here without this would have shipped it.
                      */}
                    {' '}
                    <span className="search-option-sub">{said.sub}</span>
                  </>
                );
              })()}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
